import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Upload, X, Loader2, CheckCircle } from 'lucide-react';

import { API_URL } from '../utils/api';

export default function ImageUpload({ value, onChange, label, helpText }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const getAuthToken = () => {
    // Try to get token from localStorage
    const token = localStorage.getItem('token');
    if (token) return token;
    
    // If no token in localStorage, user needs to re-login
    console.warn('No auth token found in localStorage');
    return null;
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a JPG, PNG, or WebP image');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be smaller than 5MB');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = getAuthToken();
      
      if (!token) {
        throw new Error('Session expired. Please refresh the page and log in again.');
      }
      
      console.log('Uploading with token:', token.substring(0, 20) + '...');
      
      const response = await fetch(`${API_URL}/api/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      // Read response as text first to avoid body stream issues
      const responseText = await response.text();
      
      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(responseText || 'Upload failed - server error');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Upload failed');
      }

      // Construct full URL for the image
      onChange(`${API_URL}${data.url}`);
      setError(null);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    handleFileSelect(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleRemove = () => {
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="text-sm font-medium text-slate-700 block">{label}</label>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp"
        onChange={handleInputChange}
        className="hidden"
        data-testid="image-upload-input"
      />

      {value ? (
        <div className="relative">
          <div className="relative rounded-xl overflow-hidden border-2 border-green-200 bg-green-50">
            <img
              src={value}
              alt="Uploaded"
              className="w-full h-48 object-cover"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="12">Image</text></svg>';
              }}
            />
            <div className="absolute top-2 right-2 flex gap-2">
              <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Uploaded
              </div>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={handleRemove}
                className="h-7 w-7 p-0 rounded-full"
                data-testid="remove-image-btn"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
            transition-all duration-200
            ${dragOver 
              ? 'border-violet-400 bg-violet-50' 
              : 'border-slate-300 hover:border-violet-400 hover:bg-slate-50'
            }
            ${uploading ? 'pointer-events-none opacity-70' : ''}
          `}
          data-testid="image-upload-dropzone"
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
              <p className="text-sm text-slate-600">Uploading image...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-violet-100 flex items-center justify-center">
                <Upload className="w-7 h-7 text-violet-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  JPG, PNG or WebP (max 5MB)
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="text-sm text-red-500 flex items-center gap-1">
          <X className="w-4 h-4" />
          {error}
        </p>
      )}

      {helpText && !error && (
        <p className="text-xs text-slate-400">{helpText}</p>
      )}

      <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
        <p className="font-medium mb-1">Recommended image specifications:</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>Size: <span className="font-medium">1200 x 800 pixels</span> (landscape)</li>
          <li>Format: JPG, PNG, or WebP</li>
          <li>Max file size: 5MB</li>
          <li>Images larger than 1920px will be automatically resized</li>
        </ul>
      </div>
    </div>
  );
}
